import { API_URL } from '@/config';
import axios from 'axios';
import { privateApi } from './api';
import { getToken } from './auth.service';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export interface UploadSessionView {
    id: string;
    label: string;
    expiresAt: string;
    capGb: number;
    uploadedBytes: number;
    remainingBytes: number;
    revoked: boolean;
    expired: boolean;
    active: boolean;
    createdAt: string;
}

export interface CreatedUploadSession extends UploadSessionView {
    token: string;
}

export interface PublicUploadSession {
    label: string;
    expiresAt: string;
    capGb: number;
    uploadedBytes: number;
    remainingBytes: number;
}

export interface PresignedUpload {
    filename: string;
    key: string;
    url: string;
}

export interface CreateUploadSessionInput {
    label: string;
    expiryHours: number;
    capGb: number;
}

// Plain client for the public upload page: no auth header, and no 401 -> /login
// redirect (unlike privateApi).
const publicApi = axios.create({ baseURL: API_URL });

// ---- Backoffice (super admin) ----

const getUploadSessions = async () => {
    const res = await privateApi.get<UploadSessionView[]>('/upload-session');
    return res.data;
};

const createUploadSession = async (input: CreateUploadSessionInput) => {
    const res = await privateApi.post<CreatedUploadSession>(
        '/upload-session',
        input,
    );
    return res.data;
};

const revokeUploadSession = async (id: string) => {
    const res = await privateApi.post<UploadSessionView>(
        `/upload-session/${id}/revoke`,
    );
    return res.data;
};

export const useUploadSessions = () =>
    useQuery({
        queryKey: ['upload-sessions'],
        queryFn: getUploadSessions,
        enabled: !!getToken(),
    });

export const useCreateUploadSession = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: createUploadSession,
        onSuccess: () =>
            queryClient.invalidateQueries({ queryKey: ['upload-sessions'] }),
    });
};

export const useRevokeUploadSession = () => {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: revokeUploadSession,
        onSuccess: () =>
            queryClient.invalidateQueries({ queryKey: ['upload-sessions'] }),
    });
};

// ---- Public upload page (token-gated, no login) ----

export const validateUploadToken = async (token: string) => {
    const res = await publicApi.get<PublicUploadSession>(
        `/public/upload/${token}`,
    );
    return res.data;
};

export const presignUploads = async (
    token: string,
    files: { filename: string; size: number }[],
) => {
    const res = await publicApi.post<PresignedUpload[]>(
        `/public/upload/${token}/presign`,
        { files },
    );
    return res.data;
};

// Direct browser -> S3 PUT. The bytes never touch our backend.
export const uploadFileToS3 = async (
    url: string,
    file: File,
    onProgress?: (percent: number) => void,
) => {
    await axios.put(url, file, {
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        onUploadProgress: (e) => {
            if (onProgress && e.total) {
                onProgress(Math.round((e.loaded / e.total) * 100));
            }
        },
    });
};

// Build the shareable link for a token (HashRouter route /#/upload/<token>).
export const buildUploadLink = (token: string) =>
    `${window.location.origin}/#/upload/${token}`;
